import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence, PanInfo } from 'framer-motion';
import { MapSpot } from '@/types/spots';
import { Wifi, WifiOff, MapPin, Plug, Volume2, ExternalLink, Globe, X, ChevronUp, Star, Coffee, Hotel, BookOpen, Laptop, Sparkles, Dumbbell, UtensilsCrossed } from 'lucide-react';
import { getCurrentNoise } from '@/utils/noiseMatrix';

const CATEGORY_ICONS: Record<string, React.ReactNode> = {
  cafe: <Coffee className="w-5 h-5" />,
  hotel_lobby: <Hotel className="w-5 h-5" />,
  library: <BookOpen className="w-5 h-5" />,
  coworking: <Laptop className="w-5 h-5" />,
  gym: <Dumbbell className="w-5 h-5" />,
  restaurant: <UtensilsCrossed className="w-5 h-5" />,
  other: <Sparkles className="w-5 h-5" />,
};

const CATEGORY_ICONS_SM: Record<string, React.ReactNode> = {
  cafe: <Coffee className="w-4 h-4" />,
  hotel_lobby: <Hotel className="w-4 h-4" />,
  library: <BookOpen className="w-4 h-4" />,
  coworking: <Laptop className="w-4 h-4" />,
  gym: <Dumbbell className="w-4 h-4" />,
  restaurant: <UtensilsCrossed className="w-4 h-4" />,
  other: <Sparkles className="w-4 h-4" />,
};

const CATEGORY_LABELS: Record<string, string> = {
  cafe: 'Café',
  hotel_lobby: 'Hotel Lobby',
  library: 'Library',
  coworking: 'Coworking',
  gym: 'Gym',
  restaurant: 'Restaurant',
  other: 'Other',
};


const ACCESS_LABELS: Record<string, string> = {
  free: 'Free to enter',
  purchase_required: 'Purchase required',
  members_only: 'Members only',
};

interface SpotBottomSheetProps {
  spots: MapSpot[];
  selectedSpot: MapSpot | null;
  onSelectSpot: (spot: MapSpot) => void;
  onClose: () => void;
}

const PEEK_HEIGHT = 160;
const HALF_HEIGHT = 420;

const StarRating = ({ value }: { value: number }) => (
  <div className="flex items-center gap-0.5">
    {[1, 2, 3, 4, 5].map((i) => (
      <Star
        key={i}
        className={`w-3 h-3 ${i <= Math.round(value) ? 'text-primary fill-primary' : 'text-muted-foreground/30'}`}
      />
    ))}
  </div>
);

const SpotBottomSheet = ({ spots, selectedSpot, onSelectSpot, onClose }: SpotBottomSheetProps) => {
  const [sheetHeight, setSheetHeight] = useState(PEEK_HEIGHT);
  const [isDesktop, setIsDesktop] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const check = () => setIsDesktop(window.innerWidth >= 1024);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  useEffect(() => {
    if (isDesktop) setSheetHeight(HALF_HEIGHT);
  }, [isDesktop]);

  const handleDragEnd = (_: any, info: PanInfo) => {
    if (isDesktop) return;
    const vy = info.velocity.y;
    const dy = info.offset.y;

    if (vy > 300 || dy > 80) {
      setSheetHeight(PEEK_HEIGHT);
    } else if (vy < -300 || dy < -80) {
      if (sheetHeight <= PEEK_HEIGHT) setSheetHeight(HALF_HEIGHT);
      else setSheetHeight(window.innerHeight * 0.85);
    }
  };

  const containerClasses = isDesktop
    ? 'fixed bottom-4 left-4 z-30 bg-background rounded-2xl shadow-[0_4px_30px_rgba(0,0,0,0.12)] w-[380px]'
    : 'fixed bottom-0 left-0 right-0 z-30 bg-background rounded-t-3xl shadow-[0_-4px_30px_rgba(0,0,0,0.12)]';

  const desktopHeight = selectedSpot ? 480 : HALF_HEIGHT;

  return (
    <motion.div
      className={containerClasses}
      style={!isDesktop ? { paddingBottom: 'env(safe-area-inset-bottom, 0px)' } : undefined}
      animate={{ height: isDesktop ? desktopHeight : sheetHeight }}
      transition={{ type: 'spring', damping: 30, stiffness: 300 }}
    >
      {!isDesktop && (
        <motion.div
          drag="y"
          dragConstraints={{ top: 0, bottom: 0 }}
          dragElastic={0.3}
          onDragEnd={handleDragEnd}
          className="cursor-grab active:cursor-grabbing touch-none"
        >
          <div className="flex justify-center pt-2.5 pb-1">
            <div className="w-9 h-1 bg-muted-foreground/25 rounded-full" />
          </div>
          <div className="px-5 pb-3 flex items-center justify-between">
            <p className="text-xs text-muted-foreground font-medium">
              {spots.length} spots nearby
            </p>
            {sheetHeight <= PEEK_HEIGHT && (
              <button
                onClick={() => setSheetHeight(HALF_HEIGHT)}
                className="text-xs text-primary font-medium flex items-center gap-1"
              >
                View all <ChevronUp className="w-3 h-3" />
              </button>
            )}
          </div>
        </motion.div>
      )}

      {isDesktop && (
        <div className="px-4 pt-4 pb-2 flex items-center justify-between">
          <p className="text-xs text-muted-foreground font-medium">
            {spots.length} spots nearby
          </p>
        </div>
      )}

      <div
        ref={scrollRef}
        className="overflow-y-auto px-4 pb-6"
        style={{ height: `calc(100% - ${isDesktop ? '44px' : '52px'})` }}
      >
        <AnimatePresence mode="wait">
          {selectedSpot ? (
            <motion.div
              key={`detail-${selectedSpot.id}`}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
              transition={{ duration: 0.2 }}
            >
              <button
                onClick={() => { onClose(); if (!isDesktop) setSheetHeight(HALF_HEIGHT); }}
                className="mb-3 flex items-center gap-1.5 text-xs text-muted-foreground"
              >
                <X className="w-3.5 h-3.5" /> Back to list
              </button>

              {/* Photo */}
              {selectedSpot.photos?.[0] && (
                <img
                  src={selectedSpot.photos[0]}
                  alt={selectedSpot.name}
                  className="w-full h-36 object-cover rounded-xl mb-3"
                />
              )}

              {/* Category icon + name */}
              <div className="flex items-center gap-3 mb-3">
                <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center text-primary flex-shrink-0">
                  {CATEGORY_ICONS[selectedSpot.category] || CATEGORY_ICONS.other}
                </div>
                <div>
                  <h2 className="font-funnel text-xl font-bold text-foreground leading-tight">{selectedSpot.name}</h2>
                  <p className="text-sm text-muted-foreground mt-0.5">{CATEGORY_LABELS[selectedSpot.category] || selectedSpot.category}</p>
                </div>
              </div>

              {/* Tags */}
              <div className="flex flex-wrap gap-2 mb-4">
                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-muted text-muted-foreground text-xs font-medium">
                  {ACCESS_LABELS[selectedSpot.access_type] || selectedSpot.access_type}
                </span>
                {(() => {
                  const noise = getCurrentNoise(selectedSpot.noise_matrix);
                  return noise ? (
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-muted text-muted-foreground text-xs font-medium">
                      <Volume2 className="w-3 h-3" /> {noise.label}
                    </span>
                  ) : null;
                })()}
              </div>

              {/* Amenities */}
              <div className="grid grid-cols-2 gap-3 mb-5">
                <div className="flex items-center gap-2.5 p-2.5 rounded-xl bg-muted/50">
                  <div className="text-primary">{selectedSpot.wifi_available ? <Wifi className="w-4 h-4" /> : <WifiOff className="w-4 h-4" />}</div>
                  <div>
                    <p className="text-xs text-muted-foreground">WiFi</p>
                    <p className="text-sm font-medium text-foreground">{selectedSpot.wifi_available ? 'Available' : 'Not available'}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2.5 p-2.5 rounded-xl bg-muted/50">
                  <div className="text-primary"><Plug className="w-4 h-4" /></div>
                  <div>
                    <p className="text-xs text-muted-foreground">Power</p>
                    <p className="text-sm font-medium text-foreground">{selectedSpot.power_outlets ? 'Available' : 'Not available'}</p>
                  </div>
                </div>
              </div>

              {/* Description */}
              {(selectedSpot.description || selectedSpot.editorial_summary) && (
                <p className="text-sm text-muted-foreground mb-5">
                  {selectedSpot.description || selectedSpot.editorial_summary}
                </p>
              )}

              {/* Actions */}
              <div className="flex gap-2 mb-4">
                <a
                  href={selectedSpot.google_maps_uri || `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(selectedSpot.formatted_address || selectedSpot.name)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 flex items-center justify-center gap-2 py-3 bg-primary text-primary-foreground rounded-full text-sm font-medium"
                >
                  <MapPin className="w-4 h-4" /> Get directions
                </a>
              </div>

              {/* Social links */}
              <div className="flex gap-3">
                {selectedSpot.website_uri && (
                  <a href={selectedSpot.website_uri} target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-foreground transition-colors">
                    <Globe className="w-5 h-5" />
                  </a>
                )}
              </div>
            </motion.div>
          ) : (
            <motion.div
              key="list"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="space-y-3"
            >
              {spots.map((spot) => (
                <SpotCard
                  key={spot.id}
                  spot={spot}
                  onSelect={() => {
                    onSelectSpot(spot);
                    if (!isDesktop) setSheetHeight(HALF_HEIGHT);
                  }}
                />
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
};

const SpotCard = ({ spot, onSelect }: { spot: MapSpot; onSelect: () => void }) => (
  <motion.button
    onClick={onSelect}
    className="w-full flex items-center gap-3 p-3 rounded-2xl bg-card hover:bg-muted/30 transition-colors text-left"
    whileTap={{ scale: 0.98 }}
  >
    <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0 text-primary">
      {CATEGORY_ICONS_SM[spot.category] || CATEGORY_ICONS_SM.other}
    </div>
    <div className="flex-1 min-w-0">
      <h3 className="font-semibold text-sm text-foreground truncate">{spot.name}</h3>
      <p className="text-xs text-muted-foreground truncate">{spot.short_address || spot.formatted_address}</p>
      {spot.rating != null && (
        <div className="flex items-center gap-2 mt-1">
          <span className="text-xs font-semibold text-foreground">{spot.rating}</span>
          <StarRating value={spot.rating} />
          {spot.user_rating_count != null && (
            <span className="text-[11px] text-muted-foreground">({spot.user_rating_count})</span>
          )}
        </div>
      )}
    </div>
    <ExternalLink className="w-4 h-4 text-muted-foreground/40 flex-shrink-0" />
  </motion.button>
);

export default SpotBottomSheet;
